import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import { formatDate } from '@/utils/format';
import { roleTone } from '@/utils/staff';
import type { StaffMember } from '@/types/portal';

interface PendingInvitesPanelProps {
  invites: StaffMember[];
  canManage: boolean;
  resendCounts: Record<string, number>;
  onResend: (member: StaffMember) => void;
  onRevoke: (member: StaffMember) => void;
}

export default function PendingInvitesPanel({
  invites,
  canManage,
  resendCounts,
  onResend,
  onRevoke,
}: PendingInvitesPanelProps) {
  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Pending invites
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500">Awaiting acceptance</p>
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-base text-secondary-800">
          <i className="ri-mail-open-line" />
        </span>
      </header>
      <ul className="divide-y divide-background-100">
        {invites.length === 0 ? (
          <li className="px-5 py-4 text-sm text-foreground-500">
            No invitations are waiting on a response.
          </li>
        ) : (
          invites.map((member) => (
            <li key={member.id} className="px-5 py-3.5">
              <div className="flex items-center gap-2.5">
                <Avatar src={member.avatar} alt={member.name} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground-900">{member.name}</p>
                  <p className="truncate text-[11px] text-foreground-500">{member.email}</p>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge tone={roleTone(member.roleId)}>{member.roleName}</Badge>
                <span className="text-[11px] text-foreground-400">
                  Sent {formatDate(member.invitedAt ?? member.lastActive)}
                </span>
                {(resendCounts[member.id] ?? 0) > 0 ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-foreground-400">
                    <i className="ri-refresh-line" />
                    Resent {resendCounts[member.id]}×
                  </span>
                ) : null}
              </div>
              <div className="mt-2.5 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  icon="ri-mail-send-line"
                  disabled={!canManage}
                  onClick={() => onResend(member)}
                >
                  Resend
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="ri-close-line"
                  disabled={!canManage}
                  onClick={() => onRevoke(member)}
                >
                  Revoke
                </Button>
              </div>
            </li>
          ))
        )}
      </ul>
    </section>
  );
}